import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Suggestions } from '@b2b/features/product/core/utilities/suggestion.model';
import { SuggestionsService } from '@b2b/features/product/core/services/suggestions.service';
import { Product, PRODUCT_MEDIA_TYPE } from '@b2b/features/product/core/utilities/product.model';
import { WindowRef } from '@shared/tools/window';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, Subscription, debounceTime, distinctUntilChanged, tap } from 'rxjs';
import { SearchBoxService } from '@b2b/components/cms/sections/cx-header-section/components/search-box/search-box.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MediaComponent } from '@shared/components/media/media.component';
import { ProductImageService } from '@b2b/features/product/core/services/product-image.service';

@Component({
  selector: 'search-box-modal',
  imports: [CommonModule, RouterModule, ReactiveFormsModule, TranslateModule, MediaComponent],
  templateUrl: './search-box-modal.component.html',
  styleUrl: './search-box-modal.component.scss',
})
export class SearchBoxModalComponent implements OnInit, OnDestroy {
  status$ = this.searchBoxService.getStatus();
  searchForm: UntypedFormGroup = this.fb.group({
    search: [''],
  });
  subscription = new Subscription();
  offerSearchSubscription?: Subscription;

  productResults$ = new BehaviorSubject<Suggestions | undefined>(undefined);
  showNoResults = false;
  loading: boolean = false;

  private searchTerm$ = new BehaviorSubject<string | undefined>(undefined);
  private readonly productImageService = inject(ProductImageService);
  #destroyRef = inject(DestroyRef);
  imageNotFound = this.productImageService.getImageNotFound(PRODUCT_MEDIA_TYPE.PRODUCT);

  imageProduct = (product: Product) => {
    if (!product) return this.imageNotFound;
    return this.productImageService.getImage(product, PRODUCT_MEDIA_TYPE.PRODUCT);
  };

  onImageError(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = this.imageNotFound.src;
  }

  constructor(
    protected fb: UntypedFormBuilder,
    private searchBoxService: SearchBoxService,
    private router: Router,
    private winRef: WindowRef,
    private suggestionsService: SuggestionsService,
  ) {}

  ngOnInit(): void {
    this.inputFocus();
    this.setupFormSubscriptions();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    if (this.offerSearchSubscription) {
      this.offerSearchSubscription.unsubscribe();
    }
  }

  private setupFormSubscriptions(): void {
    this.subscription.add(
      this.searchTerm$
        ?.pipe(
          tap(value => (this.loading = !!value)),
          debounceTime(500),
          distinctUntilChanged(),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe((searchTerm: string | undefined) => {
          if (searchTerm && searchTerm.length >= 1) {
            this.searchProducts(searchTerm);
            this.loading = false;
          } else {
            this.clearResults();
            this.loading = false;
          }
        }),
    );
  }

  submit() {
    const searchValue = this.searchForm.get('search')?.value;

    if (searchValue) {
      this.router.navigate(['/pesquisa'], {
        queryParams: { query: searchValue + ':score', page: 1 },
      });
    }

    this.close();
  }

  onSearch(event: Event) {
    const inputValue = (event.target as HTMLInputElement).value;
    this.searchTerm$.next(inputValue);
  }

  private searchProducts(searchTerm: string): void {
    if (searchTerm.length < 3) {
      this.productResults$.next(undefined);
      this.showNoResults = false;
      return;
    }

    this.suggestionsService.suggestionsProducts(searchTerm).subscribe({
      next: suggestions => {
        this.productResults$.next(suggestions);
        this.showNoResults = suggestions.products.length === 0;
      },
      error: _error => {
        this.showNoResults = true;
      },
    });
  }

  private clearResults(): void {
    this.productResults$.next(undefined);
    this.showNoResults = false;
  }

  navigateToSuggestion(searchValue: string): void {
    this.router.navigate(['/pesquisa'], {
      queryParams: { query: searchValue + ':score', page: 1 },
    });
    this.close();
  }

  navigateToProduct(product: Product): void {
    this.router.navigate(['produto', product.code, product.name]);
    this.close();
  }

  inputFocus() {
    this.subscription.add(
      this.status$.subscribe(status => {
        if (status) {
          setTimeout(() => {
            const $input = this.winRef.document.querySelector('.js-search') as HTMLInputElement;
            $input.focus();
          }, 50);
        }
      }),
    );
  }

  closeByContainer(el: any) {
    const isContainer =
      el && el.target && el.target.classList && el.target.classList.contains('js-container-search-modal');

    if (isContainer) {
      this.close();
    }
  }

  close() {
    this.searchBoxService.toggleStatus(false);
    this.clearResults();
    this.searchForm.reset({ search: '' });
  }

  trackByProductCode(index: number, product: Product): string {
    return product.code || index.toString();
  }

  trackBySuggestion(index: number, suggestion: any): any {
    return suggestion || index.toString();
  }
}
